import { useMemo } from 'react';
import { MessageItemController } from './messageItemInterface';
import { MessageItemViewProps } from './messageItemProps';
import { MessagePartContainerProps } from '../../part';
import { MessageContextItemContainerProps } from '../../message';

export function createMessageItemController(): MessageItemController {
    return {
        useProps(_context, item, props): MessageItemViewProps {
            const context = useMemo(() => {
                const acc = item.message.context
                    // TODO better handling of differentiation between public and hidden context
                    .filter((item) => item.kind !== 'integration')
                    .map(
                        (item): Omit<MessageContextItemContainerProps, 'as'> => ({
                            item: {
                                context: item,
                            },
                        })
                    );
                for (const part of item.message.parts) {
                    if (
                        part.type === 'tool-invocation' &&
                        part.toolInvocation.toolName === 'start_solution' &&
                        part.toolInvocation.result
                    ) {
                        for (const artifact of part.toolInvocation.result.artifacts) {
                            acc.push({
                                item: {
                                    context: {
                                        kind: 'reference',
                                        data: {
                                            document: artifact.id,
                                        },
                                    },
                                },
                            });
                        }
                    }
                }
                return acc;
            }, [item.message.context]);

            // Separate text parts and tool parts for proper ordering
            const allParts = item.message.parts.flatMap(
                (part): Omit<MessagePartContainerProps, 'as'>[] => {
                    if (part.type === 'step-start' || part.type === 'step-end') {
                        // We don't render steps
                        return [];
                    }
                    return [
                        {
                            item: {
                                definition: item.definition,
                                instance: item.instance,
                                message: item.message,
                                part,
                            },
                        },
                    ];
                }
            );

            // Sort parts: text parts first, then tool parts
            const sortedParts = allParts.sort((a, b) => {
                const aIsText = a.item.part.type === 'text';
                const bIsText = b.item.part.type === 'text';

                if (aIsText && !bIsText) return -1; // text comes first
                if (!aIsText && bIsText) return 1; // tools come last
                return 0; // maintain original order within same type
            });

            return {
                id: item.message.id,
                parts: sortedParts,
                context,
                timestamp: item.message.createdAt,
            };
        },
    };
}
